<template>
  <div>
    <es-filter :defaultExpanded="true">
      <template #filter-options>
        <div class="columns multi-dropdown-columns">
          <div class="column is-2">
            <div class="field">
              <label class="label has-text-weight-bold">
                Transaction ID :
              </label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="transactionIdInput"
                  :aria-label="transactionIdInput || 'Transaction ID'"
                >
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="field">
              <label class="label has-text-weight-bold">
                Batch ID :
              </label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="pidBatchIdInput"
                  :aria-label="pidBatchIdInput || 'Batch ID'"
                >
              </div>
            </div>
          </div>
          <div class="column is-2">
            <es-select
              api="submission-types"
              key-value="code"
              label="Submission Type : "
              ref="selSubmissionType"
              value-tpl="${description}"
              :multi="true"
              @update:values="submissionTypes = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              key-value="description"
              label="Status :"
              ref="selStatuses"
              value-tpl="${description}"
              :clearOut="true"
              :multi="true"
              :pre-selected="selectedStatuses"
              :set-values="statuses"
              @update:values="statusInput = $event"
            />
          </div>
        </div>
        <div class="columns multi-dropdown-columns">
          <div class="column is-2">
            <es-select
              api="districts"
              key-value="code"
              ref="selDistricts"
              search-min-length="2"
              :apiQueryParams="getAllDistricts"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:DISTRICT_CODE`)} :`"
              :multi="true"
              :pre-selected="districtCodes"
              :search="true"
              :value-tpl-fn="(result) => `${result.location.name} (${result.code})`"
              @update:values="districts = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              api="schools"
              :key-value="['districtCode', 'code']"
              ref="selSchools"
              search-min-length="2"
              :api-query-params="getDistrictParams"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_CODE`)} :`"
              :multi="true"
              :pre-selected="schoolCodesPromises"
              :search="true"
              :sub-value-tpl-fn="(result) =>
                `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
              :value-tpl-fn="(result) => `${result.location.name} (${result.code})`"
              @update:values="schools = $event"
            />
          </div>
          <div class="column is-2">
            <es-select
              key-value="id"
              ref="selYears"
              value-tpl="${year}"
              :clearOut="true"
              :label="`${getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_YEAR`)} :`"
              :multi="true"
              :pre-selected="schoolYearInput"
              :set-values="pidSchoolYearsDesc"
              @update:values="schoolYearInput = $event"
            />
          </div>
          <div class="column is-4">
            <div class="field no-border">
              <label class="label has-text-weight-bold">
                Process Date Range :
              </label>
              <div class="control is-flex">
                <input
                  class="input"
                  type="date"
                  v-model="startDateInput"
                  :aria-label="startDateInput || 'Start Date Input'"
                  >
                <span
                  class="is-flex flex-align--center"
                  style="padding: 0 0.5rem; font-size: 12px"
                >
                  to
                </span>
                <input
                  class="input"
                  type="date"
                  v-model="endDateInput"
                  :aria-label="endDateInput || 'End Date Input'"
                >
              </div>
            </div>
          </div>
          <div
            class="column has-padding-right"
            style="margin-top: auto"
          >
            <div class="buttons flex-justify--end">
              <button
                class="button button-link has-underline"
                @click="resetFilter"
              >
                Reset
              </button>
              <button
                class="button is-primary"
                :disabled="disableFilter"
                :title="disableFilter
                  ? 'Transaction ID, Batch ID, or Start Date is required.'
                  : null"
                @click="filterData"
              >
                Filter Data
              </button>
            </div>
          </div>
        </div>
      </template>
    </es-filter>
    <div style="padding: 0 1.25rem .5rem 2rem">
      <es-exception
        v-if="isStateUser && !startDate && !pidSubmissionId && !pidBatchId"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="Transaction ID, Batch ID, or Start Date must be entered to view results."
      />
      <pid-transactions-table
        v-else
        :columnHeaders="columnHeaders"
        :loading="loadingTransactions"
        :loadingError="loadingTransactionError"
        :transactions="transactions"
        :totalCount="transactionsTotalCount"
        @updateSorter="updateSorter"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapActions, mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsSelect from '@/components/EsSelect.vue';
import PidTransactionsTable from '@/components/PidTransactionsTable.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
// import { runInThisContext } from 'vm';

export default {
  mixins: [
    DateUtils,
    FieldLabels,
  ],
  components: {
    EsException,
    EsFilter,
    EsSelect,
    PidTransactionsTable,
  },
  props: {
    districtCodes: {
      type: Array,
    },
    endDate: {
      type: String,
    },
    pidSubmissionId: {
      type: String,
    },
    pidBatchId: {
      type: String,
    },
    page: {
      type: String,
    },
    schoolCodes: {
      type: Array,
    },
    schoolYear: {
      type: Array,
    },
    status: {
      type: Array,
    },
    startDate: {
      type: String,
    },
    sortField: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    return {
      // props and filter
      pidBatchIdInput: this.pidBatchId,
      districts: this.districtCodes.map(d => ({ code: d })),
      endDateInput: this.endDate,
      initialDate: null,
      schools: this.schoolCodes
        .map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      schoolYearInput: [],
      submissionTypes: [],
      startDateInput: this.startDate,
      statusInput: [],
      transactionIdInput: this.pidSubmissionId,

      // data for batch view
      transactions: [],
      transactionsTotalCount: null,
      loadingTransactions: false,
      loadingTransactionError: null,
      columnHeaders: [],
      userPersonTypesCodes: null,
    };
  },
  async created() {
    this.setDataElements();
    try {
      await Promise.all([
        this.loadPidTransactionStatuses(),
        this.loadPersonTypes(),
      ]);
    } catch (err) {
      // do nothing
    }
    if (this.startDate || this.pidSubmissionId || this.pidBatchId) {
      this.loadTransactions();
    }
  },
  mounted() {
    this.$root.$on('refreshMatchHistory', this.loadTransactions);
  },
  destroyed() {
    this.$root.$off('refreshMatchHistory');
  },
  watch: {
    $route() {
      if (!this.isStateUser || this.startDate || this.pidSubmissionId || this.pidBatchId) {
        this.loadTransactions();
      }
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters({
      isStateUser: 'isStateUser',
    }),
    ...mapGetters('lookups', [
      'pidSchoolYearsDesc',
    ]),
    ...mapGetters('lookups', [
      'pidNearMatchesTransGroup',
      'pidCompletedTransGroup',
      'pidCanceledTransGroup',
      'defaultPersonType',
    ]),
    dateDifference() {
      if (!this.initialDate) {
        return 29;
      }
      // Get today's date
      const today = new Date();
      // Calculate the time difference in milliseconds
      const timeDifference = today - this.initialDate;
      // Convert time difference from milliseconds to days
      const dayDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      // Return the difference in days
      return dayDifference;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      }
      return this.userPersonTypesCodes[0];
    },
    statuses() {
      const g = [];
      if (
        this.pidNearMatchesTransGroup
        && this.pidNearMatchesTransGroup.group
        && Array.isArray(this.pidNearMatchesTransGroup.list)
      ) {
        const PENDING = {
          status: 'PENDING',
          description: this.pidNearMatchesTransGroup.group,
          id: Array.isArray(this.pidNearMatchesTransGroup.list)
            ? this.pidNearMatchesTransGroup.list.map(s => s.id)
            : [],
        };
        g.push(PENDING);
      }
      if (
        this.pidCompletedTransGroup
        && this.pidCompletedTransGroup.group
        && Array.isArray(this.pidCompletedTransGroup.list)
      ) {
        const COMPLETED = {
          status: 'COMPLETED',
          description: this.pidCompletedTransGroup.group,
          id: this.pidCompletedTransGroup.list.map(s => s.id),
        };
        g.push(COMPLETED);
      }
      if (
        this.pidCanceledTransGroup
        && this.pidCanceledTransGroup.group
        && Array.isArray(this.pidCanceledTransGroup.list)
      ) {
        const CANCELED = {
          status: 'CANCELED',
          description: this.pidCanceledTransGroup.group,
          id: Array.isArray(this.pidCanceledTransGroup.list)
            ? this.pidCanceledTransGroup.list.map(s => s.id)
            : [],
        };
        g.push(CANCELED);
      }
      return g;
    },
    selectedStatuses() {
      return this.statuses
        .filter(s => this.status.find(status => status === s.status));
    },
    disableFilter() {
      return this.loadingTransactions
        || (this.isStateUser && (!this.schoolYearInput || !this.schoolYearInput.length))
        || (this.isStateUser
        && !this.startDateInput
        && !this.transactionIdInput
        && !this.pidBatchIdInput);
    },
    getAllDistricts() {
      const params = {};
      params.active = 'all';
      return params;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);
      params.active = 'all';
      return params;
    },
    hasInputParams() {
      return this.transactionIdInput || this.pidBatchIdInput || this.districts.length
        || this.endDateInput || this.page || this.schools.length || this.submissionTypes.length
        || this.startDateInput || this.schoolYearInput.length || this.statusInput.length;
    },
    searchParams() {
      const params = {};
      if (this.transactionIdInput) {
        params.pidSubmissionId = this.transactionIdInput.trim();
      }
      if (this.pidBatchIdInput) {
        params.pidBatchId = this.pidBatchIdInput.trim();
      }
      if (this.submissionTypes) {
        params.submissionTypes = this.submissionTypes.map(c => c.code);
      }
      if (this.statusInput.length) {
        params.status = this.statusInput.map(s => s.status);
      }
      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }
      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }
      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }
      if (this.startDateInput) {
        params.startDate = this.startDateInput;
      }
      if (this.endDateInput) {
        params.endDate = this.endDateInput;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};

      params.page = this.page ? this.page : undefined;
      params.pidSubmissionId = this.pidSubmissionId ? this.pidSubmissionId : undefined;
      params.pidBatchId = this.pidBatchId ? this.pidBatchId : undefined;
      params.districtCodes = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCodes = this.schoolCodes.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : undefined;
      params.statuses = this.status.length
        ? this.statuses
          .filter(s => this.status.find(statusProp => statusProp === s.status))
          .map(o => o.id)
          .flat()
        : undefined;
      params.startDate = this.startDate ? this.startDate : undefined;
      params.submissionTypes = this.submissionTypes.length
        ? this.submissionTypes.map(c => c.code) : undefined;
      params.endDate = this.endDate ? this.endDate : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);

      return params;
    },
  },
  asyncComputed: {
    async schoolCodesPromises() {
      const promises = [];
      this.schoolCodes.forEach(code => promises
        .push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPidTransactionStatuses',
      'loadPersonTypes',
    ]),
    filterData() {
      if (!this.hasInputParams && this.searchParams) {
        this.resetFilter();
      } else {
        this.$router.replace({ query: this.searchParams });
      }
    },
    getSchoolCodes() {
      return this.schoolCodes.map(code => code.split(/[:]+/)[0]);
    },
    getTransactions() {
      return axios.get('personid/transactions',
        {
          params: this.urlParams,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
    },
    async loadTransactions() {
      try {
        this.$root.$emit('matchHistoryLoading', true);
        this.loadingTransactions = true;
        this.loadingTransactionError = false;
        const response = await this.getTransactions();
        this.transactions = response.data.data;
        this.transactionsTotalCount = response.data.totalCount;
      } catch (e) {
        this.loadingTransactionError = true;
      } finally {
        this.$root.$emit('matchHistoryLoading', false);
        this.loadingTransactions = false;
      }
    },
    resetFilter() {
      // TODO: searchParams() returns all keys (even if the value is undefined)
      if (Object.keys(this.urlParams).length) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selDistricts.clear();
      this.$refs.selSchools.clear();
      this.$refs.selStatuses.clear();
      this.$refs.selYears.clear();
      this.$refs.selSubmissionType.clear();

      this.transactionIdInput = '';
      this.pidBatchIdInput = '';
      this.startDateInput = '';
      this.endDateInput = '';

      this.transactions = [];
      this.transactionsTotalCount = null;

      // set year back to current year.
      // set start date to 30 days prior.
      this.$refs.selYears.pushValues(this.pidSchoolYearsDesc.filter(y => this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'] === y.year));
      this.startDateInput = this.buildDateString(this.dateDifference);

      this.filterData();
    },
    setDataElements() {
      this.schoolYearInput = this.pidSchoolYearsDesc
        .filter(y => this.schoolYear.find(sy => sy === y.year));
      this.initialDate = this.$route.query.startDate ? new Date(this.$route.query.startDate) : null;
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      this.columnHeaders = [
        {
          header: null,
          field: null,
          style: {
            width: '55px',
          },
        },
        {
          header: 'Transaction Info',
          title: 'Transaction Info',
          field: 'id',
          order: 'DESC',
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Location Info',
          title: 'Location Info',
          field: 'districtCode',
          order: null,
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Process Info',
          title: 'Process Info',
          field: 'lastUpdateDate',
          order: null,
          sortable: true,
          style: {
            idth: '10%',
          },
        },
        {
          header: 'Batch ID',
          title: 'Batch ID',
          field: 'pidBatchId',
          order: null,
          sortable: true,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Status Info',
          title: 'Status Info',
          field: 'status',
          order: null,
          sortable: true,
        },
        {
          header: 'Actions',
          title: 'Actions',
          field: null,
          style: {
            width: '1%',
          },
        },
      ];
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
